import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';
import { InviteesGroupEnums } from '../InviteesGroup/InviteesGroup.enums';
import { upsertParticipants } from '../InviteesGroup/InviteesGroup.utils';
import useRestrictedAccessConfig from './RestrictedAccessGroup.config';
import { succesToast } from 'basics/utils/toast';
import { HandleFilesExternal, HandleRemoveItemAsync } from 'components/QuickForm/QuickForm.types';
import { AssetTarget, AssetType, RegisterParticipantInput } from 'generated/event.types';
import { commonFields } from 'modules/events/basics/constants/commonFormFields.constants';
import { InvitationSource, ParticipantStatus } from 'modules/events/basics/enums/events.enums';
import { EventStateType, Participant } from 'modules/events/basics/types/events.types';
import { transformParticipantsToQuickForm } from 'modules/events/basics/utils/participants.utils';
import { useClearParticipantStatusMutation } from 'modules/events/graphql/mutations/clearParticipantStatus';
import { useCreateParticipantsFromCSVMutation } from 'modules/events/graphql/mutations/createParticipantsFromCSV';
import { useCancelParticipantMutation } from 'modules/events/graphql/mutations/deleteParticipant';
import eventState from 'modules/events/states/events.states';
import { useYodaForm } from 'yoda-ui/yodaForm';

const useRestrictedAccessGroup = () => {
  const { PRIVILEGED_LIST } = InviteesGroupEnums;
  const { t } = useTranslation();
  const { useWatchField } = useYodaForm();
  const [executeCancelRegistrationMutation] = useCancelParticipantMutation();
  const [runClearInviteeStatus] = useClearParticipantStatusMutation();
  const [runCreateMutation] = useCreateParticipantsFromCSVMutation();
  const {
    cardSwitchConfig,
    privilegedUsersConfig,
    restrictedDescriptionConfig,
    restrictedRegistrationModeConfig,
  } = useRestrictedAccessConfig();
  const { createEvent } = useSnapshot(eventState) as EventStateType;

  const isRestrictedAccess = useWatchField(commonFields.isRestrictedAccess);

  const [selectedTab, setSelectedTab] = useState<InviteesGroupEnums>(PRIVILEGED_LIST);

  const handleFilesExternal: HandleFilesExternal = async (files, setErrorMessage) => {
    const errorMessage = t('errors_standard');
    if (!createEvent?._id) {
      setErrorMessage(errorMessage);
      return;
    }
    const { size, name } = files[0];
    const response = await runCreateMutation(
      files[0],
      {
        size,
        name,
        assetType: AssetType.document,
        assetTarget: AssetTarget.participantCsv,
      },
      createEvent._id,
      false,
    );
    if (response?.participants?.length && eventState.createEvent.data) {
      const { participants: prevParticipants } = eventState.createEvent.data;
      eventState.createEvent.data.participants = upsertParticipants(prevParticipants || [], response.participants as Participant[]);
    } else {
      setErrorMessage(errorMessage);
    }
  };

  const updateInviteesList = async (updatedParticipant: Participant) => {
    if (eventState?.createEvent?.data?.participants) {
      const indexToUpdate = eventState.createEvent.data.participants.findIndex(
        (participant) => participant.mdmId === updatedParticipant.mdmId,
      );
      if (indexToUpdate !== -1) {
        // Replace the old invitees with the updated one
        eventState.createEvent.data.participants[indexToUpdate] = updatedParticipant;
        privilegedUsersConfig.defaultValue = eventState.createEvent.data.participants.filter(
          (participant) => !participant.restricted && participant.invitationSource === InvitationSource.BO,
        );
      }
    }
    return privilegedUsersConfig.defaultValue;
  };

  const handleRemoveItemAsync: HandleRemoveItemAsync = async (id: string) => {
    let updatedList = null;
    if (createEvent?._id) {
      const selectedParticipant = createEvent.data?.participants?.find(
        (eventParticipant) => eventParticipant.mdmId === id || eventParticipant.email === id,
      );
      if (!selectedParticipant?.registrationStatusHistory?.rejected) {
        const cancelledParticipant = await executeCancelRegistrationMutation(
          {
            mdmId: selectedParticipant?.mdmId,
            email: selectedParticipant?.email,
            eventId: createEvent._id,
            firstName: selectedParticipant?.firstName,
            lastName: selectedParticipant?.lastName,
          } as RegisterParticipantInput,
        );
        if (!!cancelledParticipant && eventState.createEvent.data) {
          updatedList = await updateInviteesList(cancelledParticipant as Participant);
          if (updatedList) {
            succesToast('Invitee has been cancelled');
          }
        }
      } else {
        const updatedParticipant = await runClearInviteeStatus(
          selectedParticipant.mdmId,
          createEvent._id,
          selectedParticipant.email,
          ParticipantStatus.rejected,
        );
        if (updatedParticipant && eventState.createEvent.data) {
          updatedList = await updateInviteesList(updatedParticipant as Participant);
          if (updatedList) {
            succesToast('Invitee has been invited back');
          }
        }
      }
    }
    return updatedList ? transformParticipantsToQuickForm(updatedList) : undefined;
  };

  const tabsConfig = [
    {
      label: t('privilegedGroup_tabsConfig_uploadList_label'),
      value: PRIVILEGED_LIST,
    },
  ];

  return {
    cardSwitchConfig,
    restrictedAccessConfig: {
      ...privilegedUsersConfig,
      handleFilesExternal,
      handleRemoveItemAsync,
    },
    restrictedDescriptionConfig,
    restrictedRegistrationModeConfig,
    openedPanel: isRestrictedAccess?.value || false,
    selectedTab,
    setSelectedTab,
    t,
    tabsConfig,
  };
};

export default useRestrictedAccessGroup;
